// ══════════════════════════════════════════════════════════════════════════════
// LandingPage.js — VedSanga landing · 3 full-width sections with chips
// Visual system: one accent color (saffron), serif reserved for display
// headlines only, Inter for everything else — modeled on claude.com's
// restrained "one accent + neutrals" pattern.
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";

const ACCENT = "#E8832A";
const ACCENT_RGB = "232,131,42";

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// ── Temple silhouette watermark for hero (SVG only — no overlay text, so nothing bleeds off-edge) ──
function TempleSilhouette() {
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
      <div style={{
        position: "absolute", bottom: "0%", left: "50%", transform: "translateX(-50%)",
        width: 700, height: 500, borderRadius: "50%",
        background: "radial-gradient(ellipse, rgba(200,150,40,0.14) 0%, transparent 65%)",
      }} />
      <svg
        viewBox="0 0 800 400"
        style={{
          position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)",
          width: "100%", maxWidth: 900, height: "auto",
          opacity: 0.1, color: "#7A4A00",
        }}
        fill="#7A4A00"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="50" y="370" width="700" height="18" />
        <rect x="80" y="355" width="640" height="15" />
        <polygon points="340,355 460,355 450,260 430,200 400,120 370,200 350,260" />
        <polygon points="350,260 450,260 445,220 400,175 355,220" />
        <polygon points="355,220 445,220 440,185 400,148 360,185" />
        <polygon points="360,185 440,185 435,158 400,130 365,158" />
        <ellipse cx="400" cy="115" rx="14" ry="9" />
        <ellipse cx="400" cy="104" rx="9" ry="7" />
        <circle cx="400" cy="95" r="6" />
        <circle cx="400" cy="87" r="3" />
        <polygon points="200,355 290,355 282,290 270,240 245,190 220,240 208,290" />
        <polygon points="210,290 280,290 274,260 245,225 216,260" />
        <ellipse cx="245" cy="183" rx="10" ry="7" />
        <circle cx="245" cy="174" r="5" />
        <polygon points="510,355 600,355 592,290 580,240 555,190 530,240 518,290" />
        <polygon points="520,290 590,290 584,260 555,225 526,260" />
        <ellipse cx="555" cy="183" rx="10" ry="7" />
        <circle cx="555" cy="174" r="5" />
        <rect x="100" y="330" width="70" height="25" />
        <polygon points="100,330 170,330 152,300 118,300" />
        <rect x="630" y="330" width="70" height="25" />
        <polygon points="630,330 700,330 682,300 648,300" />
        <rect x="370" y="340" width="60" height="15" />
        <path d="M370,340 Q400,310 430,340" />
      </svg>
    </div>
  );
}

// ── SVG icon library — minimal line art, no emojis ────────────────────────────
function Icon({ name, size = 16, color = "currentColor", style: extStyle }) {
  const s = { width: size, height: size, display: "inline-block", flexShrink: 0, verticalAlign: "middle", ...extStyle };
  const lp = { fill: "none", stroke: color, strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    /* ── emotion icons ────────────────────── */
    case "anxiety":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M1 8 Q3 5 5 8 Q7 11 9 8 Q11 5 13 8 Q14 9.5 15 8"/><circle cx="8" cy="3.5" r="1.2" fill={color} stroke="none"/></svg>;
    case "heartbreak":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M8 13 C6 12 2 9.5 2 5.5 C2 3.5 3.5 2 5.5 2 C6.8 2 7.6 2.7 8 3.4 C8.4 2.7 9.2 2 10.5 2 C12.5 2 14 3.5 14 5.5 C14 9.5 10 12 8 13Z"/><path d="M8 8 L6.5 6 L8 4.5 L9.5 6 L8 8Z" stroke={color} fill="#FFFFFF"/></svg>;
    case "lostpurpose":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="8" cy="8" r="6"/><line x1="8" y1="2" x2="8" y2="4"/><line x1="8" y1="12" x2="8" y2="14"/><line x1="2" y1="8" x2="4" y2="8"/><line x1="12" y1="8" x2="14" y2="8"/><path d="M8 8 L10.5 5" strokeWidth="2"/><circle cx="8" cy="8" r="1" fill={color} stroke="none"/></svg>;
    case "anger":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M9 1.5 L5.5 8 L8 8 L5 14.5 L13 6.5 L10 6.5 Z" fill="none"/></svg>;
    case "loneliness":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="8" cy="4.5" r="2.5"/><path d="M4.5 14 C4.5 11 6 9 8 9 C10 9 11.5 11 11.5 14"/><circle cx="3" cy="6" r="1.5" strokeOpacity="0.25"/><circle cx="13" cy="6" r="1.5" strokeOpacity="0.25"/><path d="M1.5 14 C1.5 12.5 2.5 11.5 3 11" strokeOpacity="0.25"/><path d="M14.5 14 C14.5 12.5 13.5 11.5 13 11" strokeOpacity="0.25"/></svg>;
    case "hardecision":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><line x1="8" y1="2" x2="8" y2="14"/><path d="M2.5 6 L8 4 L13.5 6"/><path d="M2.5 6 L1 10 L5 10 Z"/><path d="M13.5 6 L15 10 L11 10 Z"/></svg>;
    case "grief":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M8 1.5 C8 1.5 12.5 6.5 12.5 10 C12.5 12.5 10.5 14.5 8 14.5 C5.5 14.5 3.5 12.5 3.5 10 C3.5 6.5 8 1.5 8 1.5Z"/></svg>;
    case "burnout":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M8 14 C5.5 14 4 12 4 9.5 C4 7.5 5 6 5.5 4.5 C6 6 7 7 7.5 7 C6.5 5 7 2.5 9 1.5 C8.5 3.5 9.5 5 9.5 6.5 C10.5 5.5 11 4 10.5 2.5 C12 4 12 7 12 9 C12 12 10.5 14 8 14Z"/><path d="M5.5 14 L10.5 14"/></svg>;
    /* ── spiritual feature icons ──────────── */
    case "askgita":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M2 2.5 C2 2 2.5 1.5 3 1.5 L13 1.5 C13.5 1.5 14 2 14 2.5 L14 9.5 C14 10 13.5 10.5 13 10.5 L9.5 10.5 L6.5 13.5 L6.5 10.5 L3 10.5 C2.5 10.5 2 10 2 9.5 Z"/><path d="M7 5 C7 4 7.8 3.5 8.5 3.8 C9.2 4.1 9.2 5 8.5 5.5 C8 5.8 8 6.3 8 6.8"/><circle cx="8" cy="8.2" r="0.5" fill={color} stroke="none"/></svg>;
    case "journey":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="8" cy="8" r="6"/><circle cx="8" cy="8" r="1.5" fill={color} stroke="none"/><path d="M8 8 L8 4" strokeWidth="1.8"/><path d="M8 8 L11 9.5" strokeWidth="1.3"/></svg>;
    case "themes":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M2 6 L8 2 L14 6 L14 13 C14 13.5 13.5 14 13 14 L3 14 C2.5 14 2 13.5 2 13 Z"/><path d="M6 14 L6 9 L10 9 L10 14"/></svg>;
    case "satsang":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="8" cy="4" r="2.5"/><circle cx="3.5" cy="11" r="2.5"/><circle cx="12.5" cy="11" r="2.5"/><line x1="6" y1="6" x2="4.5" y2="8.5"/><line x1="10" y1="6" x2="11.5" y2="8.5"/><line x1="6" y1="11" x2="10" y2="11"/></svg>;
    case "gitaclub":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M3 13 L3 3 C3 3 5.5 4 8 3 C10.5 4 13 3 13 3 L13 13 C10.5 12 8 13 5.5 12"/><line x1="8" y1="3" x2="8" y2="13"/></svg>;
    case "ramayana":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M2 13 C5 9 12 4 14 3"/><path d="M14 3 L11.5 6.5"/><path d="M14 3 L10.5 2"/><line x1="4.5" y1="10.5" x2="11" y2="5.5"/></svg>;
    case "panchang":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="8" cy="8" r="5.5"/><line x1="8" y1="2.5" x2="8" y2="1"/><line x1="8" y1="15" x2="8" y2="13.5"/><line x1="2.5" y1="8" x2="1" y2="8"/><line x1="15" y1="8" x2="13.5" y2="8"/><line x1="8" y1="8" x2="8" y2="5.5"/><line x1="8" y1="8" x2="10.5" y2="9.5"/></svg>;
    case "community":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="8" cy="4" r="2"/><circle cx="3" cy="10" r="1.8"/><circle cx="13" cy="10" r="1.8"/><path d="M8 6 L5 8.5"/><path d="M8 6 L11 8.5"/><line x1="4.8" y1="10" x2="11.2" y2="10"/></svg>;
    /* ── jyotish icons ────────────────────── */
    case "birthchart":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="8" cy="8" r="6.5"/><circle cx="8" cy="8" r="2.5"/><line x1="8" y1="1.5" x2="8" y2="5.5"/><line x1="8" y1="10.5" x2="8" y2="14.5"/><line x1="1.5" y1="8" x2="5.5" y2="8"/><line x1="10.5" y1="8" x2="14.5" y2="8"/><line x1="3.1" y1="3.1" x2="5.9" y2="5.9"/><line x1="10.1" y1="10.1" x2="12.9" y2="12.9"/><line x1="12.9" y1="3.1" x2="10.1" y2="5.9"/><line x1="5.9" y1="10.1" x2="3.1" y2="12.9"/></svg>;
    case "horoscope":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M3.5 8 C3.5 5.5 5 3.5 7.5 3 C5 5 5 9 7.5 11 C5 10.5 3.5 9.5 3.5 8Z" fill={color} fillOpacity="0.18"/><path d="M3.5 8 C3.5 5.5 5 3.5 7.5 3 C5 5 5 9 7.5 11 C5 10.5 3.5 9.5 3.5 8Z"/><circle cx="11" cy="5" r="1" fill={color} stroke="none"/><circle cx="13" cy="8.5" r="0.7" fill={color} stroke="none"/></svg>;
    case "dasha":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><line x1="2" y1="13" x2="14" y2="13"/><line x1="2" y1="13" x2="2" y2="3"/><path d="M2 10.5 L5 7.5 L8 9.5 L11 5.5 L14 3"/><circle cx="5" cy="7.5" r="1" fill={color} stroke="none"/><circle cx="11" cy="5.5" r="1" fill={color} stroke="none"/></svg>;
    case "muhurta":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="8" cy="9.5" r="5.5"/><line x1="8" y1="9.5" x2="8" y2="6"/><line x1="8" y1="9.5" x2="11" y2="9.5"/><path d="M5.5 2 L10.5 2"/><line x1="8" y1="2" x2="8" y2="4"/></svg>;
    case "kundali":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="6" cy="8" r="4.5"/><circle cx="10" cy="8" r="4.5"/></svg>;
    case "sadhana":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><polygon points="8,1.5 9.5,5.8 14,5.8 10.5,8.5 12,12.8 8,10 4,12.8 5.5,8.5 2,5.8 6.5,5.8"/></svg>;
    /* ── puja chip icons ──────────────────── */
    case "trishul":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><line x1="8" y1="5" x2="8" y2="15"/><path d="M5 2.5 C5 2.5 5 6 8 5 C11 6 11 2.5 11 2.5"/><line x1="5" y1="2.5" x2="5" y2="6"/><line x1="11" y1="2.5" x2="11" y2="6"/><line x1="6" y1="12" x2="10" y2="12"/></svg>;
    case "lotus":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M8 12 C8 12 5 10 5 7 C5 5 6.5 4 8 5 C9.5 4 11 5 11 7 C11 10 8 12 8 12Z"/><path d="M8 12 C8 12 3.5 11 2.5 8.5 C2 7 3.5 5.5 5 7"/><path d="M8 12 C8 12 12.5 11 13.5 8.5 C14 7 12.5 5.5 11 7"/><line x1="8" y1="12" x2="8" y2="14.5"/></svg>;
    case "conch":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M6.5 13 C3.5 12 2.5 9 3.5 6 C4.5 3 7.5 1.5 10.5 2.5 C13 3.5 14 6 13 9 C12.5 11 10.5 13 8.5 13 C7.5 13 7 12.5 7.5 11.5 C8.5 9.5 11 8.5 11 6.5 C11 5 10 4 8.5 4.5"/><path d="M6.5 13 L5.5 15"/></svg>;
    case "saturn":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="8" cy="8" r="3.5"/><path d="M1.5 10.5 C3 7.5 13 5 14.5 8"/></svg>;
    case "flame":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M8 14.5 C5.5 14.5 3.5 12.5 3.5 10 C3.5 7.5 5 6 5.5 4.5 C5.5 6 6.5 7 7 7 C6 5 6.5 2 9 1 C8.5 3 10 4.5 10 6.5 C11 5.5 11.5 4 11 2.5 C13 4.5 12.5 8 12.5 10 C12.5 12.5 10.5 14.5 8 14.5Z"/></svg>;
    case "house":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M2 8 L8 2 L14 8"/><path d="M3.5 7.5 L3.5 14 L12.5 14 L12.5 7.5"/><path d="M6 14 L6 10 L10 10 L10 14"/></svg>;
    case "crescent":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M10.5 3.5 C8 3.5 5.5 5.5 5.5 9 C5.5 12 7.5 14 10.5 14 C8.5 13 7 11.5 7 9 C7 6 8.5 4.5 10.5 3.5Z" fill={color} fillOpacity="0.15"/><path d="M10.5 3.5 C8 3.5 5.5 5.5 5.5 9 C5.5 12 7.5 14 10.5 14 C8.5 13 7 11.5 7 9 C7 6 8.5 4.5 10.5 3.5Z"/></svg>;
    /* ── puja feature icons ───────────────── */
    case "livepuja":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M8 11 C6 11 5 9.5 5 8 C5 6 6.5 4 8 4 C9.5 4 11 6 11 8 C11 9.5 10 11 8 11Z"/><line x1="8" y1="11" x2="8" y2="14"/><line x1="5.5" y1="14" x2="10.5" y2="14"/><path d="M3.5 4 C2 5.5 1.5 7.5 2 9.5"/><path d="M12.5 4 C14 5.5 14.5 7.5 14 9.5"/></svg>;
    case "priest":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="8" cy="4" r="2.5"/><path d="M3.5 14 L3.5 11 C3.5 9 5.5 7.5 8 7.5 C10.5 7.5 12.5 9 12.5 11 L12.5 14"/><polyline points="9.5,11.5 11,13 13.5,10.5"/></svg>;
    case "scroll":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M3.5 3 C3.5 2 4.5 1.5 5 1.5 L13 1.5 C13.5 1.5 14 2 14 3 L14 13 C14 13.5 13.5 14 13 14 L5 14 C4 14 3.5 13 3.5 12"/><path d="M3.5 12 C3.5 11 3 10 2.5 9.5 C2 9 2 7 3.5 7 L3.5 3"/><line x1="6.5" y1="5" x2="11" y2="5"/><line x1="6.5" y1="7.5" x2="11" y2="7.5"/><line x1="6.5" y1="10" x2="9" y2="10"/></svg>;
    case "bell":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><path d="M8 2 L8 3.5"/><path d="M4 11 L4 8 C4 5.8 5.8 3.5 8 3.5 C10.2 3.5 12 5.8 12 8 L12 11 L13.5 12.5 L2.5 12.5 Z"/><path d="M6.5 12.5 C6.5 13.3 7.2 14 8 14 C8.8 14 9.5 13.3 9.5 12.5"/></svg>;
    case "family":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="5" cy="4" r="1.8"/><circle cx="11" cy="4" r="1.8"/><circle cx="8" cy="9" r="1.5"/><path d="M2 13.5 C2 11 3.5 9.5 5 9.5"/><path d="M8 10.5 L8 13.5"/><path d="M14 13.5 C14 11 12.5 9.5 11 9.5"/></svg>;
    case "calendar":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><rect x="2" y="3" width="12" height="11" rx="1.5"/><line x1="2" y1="7" x2="14" y2="7"/><line x1="5" y1="1.5" x2="5" y2="4.5"/><line x1="11" y1="1.5" x2="11" y2="4.5"/><circle cx="5.5" cy="10" r="0.6" fill={color} stroke="none"/><circle cx="8" cy="10" r="0.6" fill={color} stroke="none"/><circle cx="10.5" cy="10" r="0.6" fill={color} stroke="none"/></svg>;
    /* ── section / nav icons ──────────────── */
    case "om":
      return <svg style={s} viewBox="0 0 16 16" fill={color}><text x="2" y="13" fontSize="13" fontFamily="serif">ॐ</text></svg>;
    case "star4":
      return <svg style={s} viewBox="0 0 16 16" fill={color}><path d="M8 1 L9 7 L15 8 L9 9 L8 15 L7 9 L1 8 L7 7 Z"/></svg>;
    case "diya":
      return <svg style={s} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10 C4 10 3 6 6 5 C8 4.5 8 6 8 6"/><path d="M8 6 C8 6 8 4.5 10 5 C13 6 12 10 12 10"/><path d="M3 10 L13 10 L12 13 L4 13 Z"/><path d="M8 6 L8 3.5"/><path d="M7 2.5 C7 2.5 7.5 1 8.5 2 C9.5 3 8.5 3.5 8 3.5"/></svg>;
    case "check":
      return <svg style={s} viewBox="0 0 16 16" {...lp}><circle cx="8" cy="8" r="6.5"/><path d="M5.5 8.2 L7.2 9.9 L10.5 6.2"/></svg>;
    default:
      return null;
  }
}

// ── Section tag label — single accent, no per-section color ───────────────────
function SectionTag({ icon, label, sanskrit }) {
  return (
    <div style={{ marginBottom: "8px" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: "8px",
        background: `rgba(${ACCENT_RGB},0.1)`,
        border: `1px solid rgba(${ACCENT_RGB},0.35)`,
        borderRadius: "20px", padding: "6px 18px",
        fontFamily: "'Inter',sans-serif", fontWeight: "600",
        fontSize: "0.95rem", color: "#8B5A15", letterSpacing: "0.04em",
        textTransform: "uppercase", marginBottom: "6px",
        flexWrap: "wrap", maxWidth: "100%",
      }}>
        {icon} {label}
      </div>
      <div style={{ fontFamily: "'Noto Sans Devanagari',serif", fontSize: "1.1rem", color: "#555544" }}>
        {sanskrit}
      </div>
    </div>
  );
}

// ── Data ──────────────────────────────────────────────────────────────────────
const JOURNEYS = [
  { icon: "anxiety",     label: "Anxiety & fear" },
  { icon: "heartbreak",  label: "Heartbreak" },
  { icon: "lostpurpose", label: "Lost purpose" },
  { icon: "anger",       label: "Anger" },
  { icon: "loneliness",  label: "Loneliness" },
  { icon: "hardecision", label: "Hard decision" },
  { icon: "grief",       label: "Grief" },
  { icon: "burnout",     label: "Burnout" },
];

const RASHIS = [
  { sym: "♈", name: "Mesh", sub: "Aries" },
  { sym: "♉", name: "Vrishabh", sub: "Taurus" },
  { sym: "♊", name: "Mithun", sub: "Gemini" },
  { sym: "♋", name: "Karka", sub: "Cancer" },
  { sym: "♌", name: "Simha", sub: "Leo" },
  { sym: "♍", name: "Kanya", sub: "Virgo" },
  { sym: "♎", name: "Tula", sub: "Libra" },
  { sym: "♏", name: "Vrishchik", sub: "Scorpio" },
  { sym: "♐", name: "Dhanu", sub: "Sagittarius" },
  { sym: "♑", name: "Makar", sub: "Capricorn" },
  { sym: "♒", name: "Kumbh", sub: "Aquarius" },
  { sym: "♓", name: "Meen", sub: "Pisces" },
];

const PUJA_CHIPS = [
  { icon: "trishul",  name: "Shiva Abhishekam" },
  { icon: "lotus",    name: "Hanuman Puja" },
  { icon: "lotus",    name: "Lakshmi Puja" },
  { icon: "conch",    name: "Vishnu Sahasranama" },
  { icon: "saturn",   name: "Shani Puja" },
  { icon: "flame",    name: "Homa / Yagna" },
  { icon: "house",    name: "Grihapravesh" },
  { icon: "crescent", name: "Ekadashi Vrata" },
];

// ══════════════════════════════════════════════════════════════════════════════
// MAIN LANDING PAGE
// ══════════════════════════════════════════════════════════════════════════════
export default function LandingPage({ onGetStarted, onSignIn, onGoPuja, onGoJyotish }) {
  const [hovJourney, setHovJourney]   = useState(null);
  const [hovRashi,   setHovRashi]     = useState(null);
  const [hovPuja,    setHovPuja]      = useState(null);
  const [hovCard,    setHovCard]      = useState(null);
  const width = useWindowWidth();
  const isMobile = width < 768;

  return (
    <div style={{
      minHeight: "100vh",
      background: "#FFFDF8",
      color: "#111111",
      fontFamily: "'Inter',sans-serif",
    }}>

      {/* ── Nav ────────────────────────────────────────────────────────────── */}
      <nav style={{
        position: isMobile ? "relative" : "sticky", top: 0, zIndex: 100,
        background: isMobile ? "#FFFFFF" : "rgba(255,255,255,0.85)",
        backdropFilter: isMobile ? "none" : "blur(18px)",
        WebkitBackdropFilter: isMobile ? "none" : "blur(18px)",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 1px 12px rgba(0,0,0,0.04)",
        padding: isMobile ? "10px 14px" : "10px 32px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: "8px",
        minHeight: "68px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? "6px" : "10px" }}>
          <span style={{
            fontFamily: "'Noto Sans Devanagari',serif", fontSize: isMobile ? "1.2rem" : "1.5rem",
            color: "#C17020", filter: "drop-shadow(0 0 6px rgba(193,112,32,0.35))",
          }}>ॐ</span>
          <div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: "700", fontSize: isMobile ? "1.05rem" : "1.25rem", color: "#000000" }}>Vedsanga</div>
            <div style={{ fontSize: "1rem", color: "#6B5A2A", letterSpacing: "0.06em" }}>वेदसङ्ग</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: isMobile ? "8px" : "12px", alignItems: "center" }}>
          <button onClick={onSignIn} style={{
            background: "transparent", border: "1px solid rgba(0,0,0,0.2)", borderRadius: "8px",
            padding: isMobile ? "8px 14px" : "12px 22px", color: "#222222", fontFamily: "'Inter',sans-serif",
            fontSize: isMobile ? "1rem" : "1.1rem", cursor: "pointer", whiteSpace: "nowrap",
          }}>Sign in</button>
          <button onClick={onGetStarted} style={{
            background: ACCENT,
            border: "none", borderRadius: "8px", padding: isMobile ? "8px 16px" : "12px 24px",
            color: "white", fontFamily: "'Inter',sans-serif", fontWeight: "600",
            fontSize: isMobile ? "1rem" : "1.1rem", cursor: "pointer",
            whiteSpace: "nowrap",
          }}>Begin →</button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section style={{
        position: "relative",
        minHeight: "72vh",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        textAlign: "center", padding: isMobile ? "70px 22px 80px" : "90px 32px 100px",
        overflow: "clip",
        background: "#FFFFFF",
      }}>
        {!isMobile && <TempleSilhouette />}

        <div style={{ position: "relative", zIndex: 1, maxWidth: 720 }}>
          <div style={{
            fontFamily: "'Noto Sans Devanagari',serif",
            fontSize: "clamp(1.1rem, 2.5vw, 1.25rem)",
            color: "#8B5A15", marginBottom: "28px", letterSpacing: "0.04em",
          }}>
            कर्मण्येवाधिकारस्ते मा फलेषु कदाचन
          </div>

          <h1 style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: "clamp(2.1rem, 5.5vw, 3.3rem)",
            color: "#000000", lineHeight: 1.2, marginBottom: "16px", fontWeight: "700",
            wordBreak: "break-word",
          }}>
            Your complete<br />
            <span style={{ color: ACCENT }}>spiritual home</span>
          </h1>

          <p style={{
            fontFamily: "'Inter',sans-serif",
            fontSize: isMobile ? "clamp(1.1rem, 4vw, 1.3rem)" : "clamp(1.2rem, 2.4vw, 1.35rem)",
            color: "#333333", lineHeight: 1.7,
            maxWidth: 540, margin: "0 auto 32px",
          }}>
            Three paths, one home: Gita wisdom, Vedic astrology, sacred puja. Explore free — sign in only to save your progress.
          </p>

          {/* Path cards — identical styling; differentiated only by photo + copy, not color */}
          <div style={{
            display: "flex", gap: "16px", flexWrap: "wrap",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : undefined,
            justifyContent: "center", marginTop: "32px",
          }}>
            {/* Card 1 — Bhagavad Gita */}
            <div
              onClick={onGetStarted}
              onMouseEnter={() => setHovCard(0)}
              onMouseLeave={() => setHovCard(null)}
              style={{
                width: isMobile ? "100%" : "210px",
                maxWidth: isMobile ? "360px" : undefined,
                padding: isMobile ? "18px 14px" : "24px 20px",
                borderRadius: "12px",
                background: "#FFFFFF",
                border: hovCard === 0
                  ? `1px solid rgba(${ACCENT_RGB},0.4)`
                  : "1px solid rgba(0,0,0,0.10)",
                cursor: "pointer",
                transition: "border-color 0.2s, box-shadow 0.2s",
                textAlign: "center",
                boxShadow: hovCard === 0
                  ? "0 8px 24px rgba(0,0,0,0.10)"
                  : "0 2px 10px rgba(0,0,0,0.05)",
              }}
            >
              <img src="/images/deities/vishnu.jpg" alt="Lord Vishnu" style={{
                width: "64px", height: "64px", borderRadius: "50%",
                objectFit: "cover", objectPosition: "top center",
                border: "3px solid #EFE6D4", margin: "0 auto 14px", display: "block",
              }} />
              <div style={{
                fontFamily: "'Inter',sans-serif", fontSize: "1.15rem",
                color: "#1A1A1A", fontWeight: "600",
              }}>Bhagavad Gita</div>
              <div style={{
                fontFamily: "'Inter',sans-serif", fontSize: "1.05rem",
                color: "#4A4A4A", lineHeight: 1.6, marginTop: "8px",
              }}>21-day journeys for life's hardest moments. Satsang circles for family.</div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.05rem", color: ACCENT, marginTop: "16px", fontWeight: 600 }}>Explore →</div>
            </div>

            {/* Card 2 — Vedic Astrology */}
            <div
              onClick={onGoJyotish}
              onMouseEnter={() => setHovCard(1)}
              onMouseLeave={() => setHovCard(null)}
              style={{
                width: isMobile ? "100%" : "210px",
                maxWidth: isMobile ? "360px" : undefined,
                padding: isMobile ? "18px 14px" : "24px 20px",
                borderRadius: "12px",
                background: "#FFFFFF",
                border: hovCard === 1
                  ? `1px solid rgba(${ACCENT_RGB},0.4)`
                  : "1px solid rgba(0,0,0,0.10)",
                cursor: "pointer",
                transition: "border-color 0.2s, box-shadow 0.2s",
                textAlign: "center",
                boxShadow: hovCard === 1
                  ? "0 8px 24px rgba(0,0,0,0.10)"
                  : "0 2px 10px rgba(0,0,0,0.05)",
              }}
            >
              <img src="/images/deities/surya.jpg" alt="Surya Deva" style={{
                width: "64px", height: "64px", borderRadius: "50%",
                objectFit: "cover", objectPosition: "top center",
                border: "3px solid #EFE6D4", margin: "0 auto 14px", display: "block",
              }} />
              <div style={{
                fontFamily: "'Inter',sans-serif", fontSize: "1.15rem",
                color: "#1A1A1A", fontWeight: "600",
              }}>Vedic Astrology</div>
              <div style={{
                fontFamily: "'Inter',sans-serif", fontSize: "1.05rem",
                color: "#4A4A4A", lineHeight: 1.6, marginTop: "8px",
              }}>Birth chart, daily horoscope, Dasha periods. No login needed.</div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.05rem", color: ACCENT, marginTop: "16px", fontWeight: 600 }}>Explore →</div>
            </div>

            {/* Card 3 — Sacred Puja */}
            <div
              onClick={onGoPuja}
              onMouseEnter={() => setHovCard(2)}
              onMouseLeave={() => setHovCard(null)}
              style={{
                width: isMobile ? "100%" : "210px",
                maxWidth: isMobile ? "360px" : undefined,
                padding: isMobile ? "18px 14px" : "24px 20px",
                borderRadius: "12px",
                background: "#FFFFFF",
                border: hovCard === 2
                  ? `1px solid rgba(${ACCENT_RGB},0.4)`
                  : "1px solid rgba(0,0,0,0.10)",
                cursor: "pointer",
                transition: "border-color 0.2s, box-shadow 0.2s",
                textAlign: "center",
                boxShadow: hovCard === 2
                  ? "0 8px 24px rgba(0,0,0,0.10)"
                  : "0 2px 10px rgba(0,0,0,0.05)",
              }}
            >
              <img src="/images/deities/shiva.jpg" alt="Shiva" style={{
                width: "64px", height: "64px", borderRadius: "50%",
                objectFit: "cover", objectPosition: "center 30%",
                border: "3px solid #EFE6D4", margin: "0 auto 14px", display: "block",
              }} />
              <div style={{
                fontFamily: "'Inter',sans-serif", fontSize: "1.15rem",
                color: "#1A1A1A", fontWeight: "600",
              }}>Sacred Puja</div>
              <div style={{
                fontFamily: "'Inter',sans-serif", fontSize: "1.05rem",
                color: "#4A4A4A", lineHeight: 1.6, marginTop: "8px",
              }}>Book verified priests for live online rituals, any occasion.</div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.05rem", color: ACCENT, marginTop: "16px", fontWeight: 600 }}>Explore →</div>
            </div>
          </div>

          {/* WhatsApp CTA — secondary, outline style so it doesn't compete with the 3 path cards */}
          <div style={{ marginTop: "24px", display: "flex", justifyContent: "center" }}>
            <a
              href="https://wa.me/15556469286?text=Namaste"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                padding: "11px 20px", borderRadius: "50px",
                background: "transparent",
                border: "1px solid rgba(37,211,102,0.45)",
                color: "#1a7a43",
                fontFamily: "'Inter',sans-serif",
                fontSize: "1.05rem",
                cursor: "pointer",
                textDecoration: "none",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Also on WhatsApp — Daily Verse · Panchang · Ask Krishna
            </a>
          </div>

          {/* Honest trust row — only claims that are actually true, no invented stats */}
          <div style={{
            marginTop: "20px", display: "flex", flexWrap: "wrap",
            justifyContent: "center", gap: "10px 22px",
          }}>
            {[
              "Free to explore",
              "No login required to start",
              "Priests verified before approval",
              "Private & secure",
            ].map((t, i) => (
              <div key={i} style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                fontFamily: "'Inter',sans-serif", fontSize: "1rem", color: "#5A4A30",
              }}>
                <Icon name="check" size={16} color="#8A7040" /> {t}
              </div>
            ))}
          </div>
        </div>

        <div style={{
          position: "absolute", bottom: "28px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",
          fontFamily: "'Inter',sans-serif",
          color: "#9A7040", opacity: 0.6, fontSize: "1rem",
        }}>
          <div>scroll</div><div>↓</div>
        </div>
      </section>

      {/* ── How it works — plain, honest, no jargon ───────────────────────────── */}
      <section style={{
        padding: isMobile ? "48px 22px" : "56px 32px",
        borderTop: "1px solid rgba(201,149,58,0.3)",
        borderBottom: "1px solid rgba(201,149,58,0.3)",
        background: "#FDF9F2",
      }}>
        <div style={{
          maxWidth: 1000, margin: "0 auto",
          display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: isMobile ? "28px" : "40px",
        }}>
          {[
            { n: "1", title: "Pick a path", body: "Bhagavad Gita, Vedic Astrology, or Puja booking — start wherever your question is." },
            { n: "2", title: "Explore for free", body: "No signup wall. Browse, read, and try things out before you decide anything." },
            { n: "3", title: "Sign in only when you save", body: "Want to keep your progress or book a priest? That's the only time we ask you to log in." },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: isMobile ? "left" : "center" }}>
              <div style={{
                width: "40px", height: "40px", borderRadius: "50%",
                background: ACCENT, color: "white",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'Inter',sans-serif", fontWeight: "700", fontSize: "1.15rem",
                margin: isMobile ? "0 0 12px" : "0 auto 14px",
              }}>{s.n}</div>
              <div style={{
                fontFamily: "'Inter',sans-serif", fontWeight: "700", fontSize: "1.2rem",
                color: "#1A1A1A", marginBottom: "6px",
              }}>{s.title}</div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1.05rem", color: "#5A4A30", lineHeight: 1.6 }}>{s.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 1 — SPIRITUAL PLATFORM
          ══════════════════════════════════════════════════════════════════ */}
      <section style={{
        padding: isMobile ? "60px 22px 70px" : "80px 32px 90px",
        background: "linear-gradient(180deg, #FFFDF8 0%, #FFFFFF 100%)",
      }}>
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: isMobile ? "44px" : "64px", alignItems: "center",
        }}>
          {/* Left: text + features */}
          <div>
            <SectionTag icon={<Icon name="om" size={18} color={ACCENT}/>} label="Family Spiritual Platform" sanskrit="आध्यात्मिक परिवार" />
            <h2 style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: "clamp(1.85rem, 4vw, 2.6rem)",
              color: "#111111", lineHeight: 1.2, margin: "18px 0 18px",
              fontWeight: "700",
            }}>
              When life feels<br /><span style={{ color: ACCENT }}>too heavy</span>
            </h2>
            <p style={{ fontFamily: "'Inter',sans-serif", color: "#444444", fontSize: "1.1rem", lineHeight: 1.85, marginBottom: "26px" }}>
              A 21-day Bhagavad Gita companion for the questions you carry alone — grief, confusion, fear, emptiness, and everything in between. With Satsang circles for your family.
            </p>
            <button onClick={onGetStarted} style={{
              padding: "14px 36px", borderRadius: "10px",
              background: ACCENT,
              border: "none", color: "white",
              fontFamily: "'Inter',sans-serif", fontWeight: "600", fontSize: "1.1rem",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}>
              Begin your journey →
            </button>
          </div>

          {/* Right: emotion chips */}
          <div>
            <div style={{
              fontFamily: "'Inter',sans-serif", fontWeight: "700",
              fontSize: "1.2rem", color: ACCENT,
              marginBottom: "10px",
            }}>
              What are you carrying right now?
            </div>
            <div style={{ fontFamily: "'Inter',sans-serif", color: "#555544", fontSize: "1rem", marginBottom: "24px" }}>
              There is a journey built for each of these
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
              {JOURNEYS.map((j, i) => (
                <div
                  key={i}
                  onMouseEnter={() => setHovJourney(i)}
                  onMouseLeave={() => setHovJourney(null)}
                  onClick={onGetStarted}
                  style={{
                    padding: "11px 22px", borderRadius: "50px",
                    border: `1px solid ${hovJourney === i ? ACCENT : "rgba(0,0,0,0.12)"}`,
                    background: hovJourney === i ? `rgba(${ACCENT_RGB},0.1)` : "#FFFFFF",
                    color: hovJourney === i ? ACCENT : "#111111",
                    cursor: "pointer", transition: "border-color 0.2s, background 0.2s, color 0.2s",
                    fontFamily: "'Inter',sans-serif",
                    fontSize: "1.05rem", display: "flex", alignItems: "center", gap: "8px",
                  }}
                >
                  <Icon name={j.icon} size={16} color={hovJourney === i ? ACCENT : "#555544"}/> {j.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Verse divider ──────────────────────────────────────────────────────── */}
      <div style={{
        padding: "40px 32px", borderTop: "1px solid rgba(201,149,58,0.3)",
        borderBottom: "1px solid rgba(201,149,58,0.3)", textAlign: "center",
        background: `linear-gradient(90deg, transparent, rgba(${ACCENT_RGB},0.03), transparent)`,
      }}>
        <span style={{
          fontFamily: "'Noto Sans Devanagari',serif",
          fontSize: "1.1rem", color: ACCENT, opacity: 0.6,
          letterSpacing: "0.04em",
        }}>
          ✦ &nbsp; नैनं छिन्दन्ति शस्त्राणि नैनं दहति पावकः &nbsp; ✦
        </span>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1rem", color: "#555544", marginTop: "8px", fontStyle: "italic" }}>
          "Weapons cannot cut this soul, nor can fire burn it." — Gita 2.23
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 2 — JYOTISH / ASTROLOGY
          ══════════════════════════════════════════════════════════════════ */}
      <section style={{
        padding: isMobile ? "60px 22px 70px" : "80px 32px 90px",
        background: "#FFFFFF",
      }}>
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: isMobile ? "44px" : "64px", alignItems: "center",
        }}>
          {/* Left: rashi grid */}
          <div>
            <div style={{
              fontFamily: "'Inter',sans-serif", fontWeight: "700", fontSize: "1.2rem",
              color: ACCENT, marginBottom: "10px",
            }}>
              Which is your Janma Rashi?
            </div>
            <div style={{ fontFamily: "'Inter',sans-serif", color: "#555544", fontSize: "1rem", marginBottom: "22px" }}>
              Enter your birth date to find out
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {RASHIS.map((r, i) => (
                <div
                  key={i}
                  onMouseEnter={() => setHovRashi(i)}
                  onMouseLeave={() => setHovRashi(null)}
                  onClick={onGoJyotish}
                  style={{
                    padding: isMobile ? "8px 14px" : "10px 18px", borderRadius: "50px",
                    border: `1px solid ${hovRashi === i ? ACCENT : "rgba(0,0,0,0.12)"}`,
                    background: hovRashi === i ? `rgba(${ACCENT_RGB},0.1)` : "#FFFFFF",
                    cursor: "pointer", transition: "border-color 0.2s, background 0.2s",
                    display: "flex", alignItems: "center", gap: "7px",
                    fontFamily: "'Inter',sans-serif",
                    fontSize: isMobile ? "1.05rem" : undefined,
                  }}
                >
                  <span style={{ fontSize: "1.1rem", color: hovRashi === i ? ACCENT : "#666666" }}>
                    {r.sym}
                  </span>
                  <div>
                    <div style={{ color: hovRashi === i ? ACCENT : "#1A1A1A", fontFamily: "'Inter',sans-serif", fontWeight: "600", fontSize: "1.05rem" }}>
                      {r.name}
                    </div>
                    <div style={{ fontSize: "0.95rem", color: "#666666" }}>{r.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: text + features */}
          <div>
            <SectionTag icon={<Icon name="star4" size={16} color={ACCENT}/>} label="Jyotish · Astrology" sanskrit="ज्योतिष" />
            <h2 style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: "clamp(1.85rem, 4vw, 2.6rem)",
              color: "#111111", lineHeight: 1.2, margin: "18px 0 18px",
              fontWeight: "700",
            }}>
              Your cosmic blueprint,<br /><span style={{ color: ACCENT }}>decoded</span>
            </h2>
            <p style={{ fontFamily: "'Inter',sans-serif", color: "#444444", fontSize: "1.1rem", lineHeight: 1.85, marginBottom: "26px" }}>
              Traditional Vedic astrology — not Western sun signs. Enter your birth date and get your Kundali, Moon sign archetype, and daily horoscope. No login needed to start.
            </p>
            <button onClick={onGoJyotish} style={{
              padding: "14px 36px", borderRadius: "10px",
              background: ACCENT,
              border: "none", color: "white",
              fontFamily: "'Inter',sans-serif", fontWeight: "600", fontSize: "1.1rem",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}>
              Discover your Rashi →
            </button>
          </div>
        </div>
      </section>

      {/* ── Second verse divider ──────────────────────────────────────────────── */}
      <div style={{
        padding: "40px 32px", borderTop: "1px solid rgba(201,149,58,0.3)",
        borderBottom: "1px solid rgba(201,149,58,0.3)", textAlign: "center",
        background: `linear-gradient(90deg, transparent, rgba(${ACCENT_RGB},0.03), transparent)`,
      }}>
        <span style={{
          fontFamily: "'Noto Sans Devanagari',serif",
          fontSize: "1.1rem", color: ACCENT, opacity: 0.6,
          letterSpacing: "0.04em",
        }}>
          ✦ &nbsp; ज्योतिषां रविरादित्यो ज्योतिर्मयेन चक्षुषा &nbsp; ✦
        </span>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: "1rem", color: "#555544", marginTop: "8px", fontStyle: "italic" }}>
          "Among lights I am the radiant sun." — Gita 10.21
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 3 — PUJA
          ══════════════════════════════════════════════════════════════════ */}
      <section style={{
        padding: isMobile ? "60px 22px 70px" : "80px 32px 90px",
        background: "linear-gradient(180deg, #FFFDF8 0%, #FFFFFF 100%)",
      }}>
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: isMobile ? "44px" : "64px", alignItems: "center",
        }}>
          {/* Left: text + features */}
          <div>
            <SectionTag icon={<Icon name="diya" size={18} color={ACCENT}/>} label="Puja & Rituals" sanskrit="पूजा" />
            <h2 style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: "clamp(1.85rem, 4vw, 2.6rem)",
              color: "#111111", lineHeight: 1.2, margin: "18px 0 18px",
              fontWeight: "700",
            }}>
              Sacred ceremonies,<br /><span style={{ color: ACCENT }}>your home</span>
            </h2>
            <p style={{ fontFamily: "'Inter',sans-serif", color: "#444444", fontSize: "1.1rem", lineHeight: 1.85, marginBottom: "26px" }}>
              Book a live online puja with a verified priest in minutes. Browse the catalog, choose your tradition and language — personalized for your state. Login only when you confirm.
            </p>
            <button onClick={onGoPuja} style={{
              padding: "14px 36px", borderRadius: "10px",
              background: ACCENT,
              border: "none", color: "white",
              fontFamily: "'Inter',sans-serif", fontWeight: "600", fontSize: "1.1rem",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}>
              Explore pujas →
            </button>
          </div>

          {/* Right: puja chips */}
          <div>
            <div style={{
              fontFamily: "'Inter',sans-serif", fontWeight: "700", fontSize: "1.2rem",
              color: ACCENT, marginBottom: "10px",
            }}>
              Select your puja
            </div>
            <div style={{ fontFamily: "'Inter',sans-serif", color: "#555544", fontSize: "1rem", marginBottom: "22px" }}>
              50+ rituals · personalized for your tradition
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
              {PUJA_CHIPS.map((p, i) => (
                <div
                  key={i}
                  onMouseEnter={() => setHovPuja(i)}
                  onMouseLeave={() => setHovPuja(null)}
                  onClick={onGoPuja}
                  style={{
                    padding: "11px 22px", borderRadius: "50px",
                    border: `1px solid ${hovPuja === i ? ACCENT : "rgba(0,0,0,0.12)"}`,
                    background: hovPuja === i ? `rgba(${ACCENT_RGB},0.1)` : "#FFFFFF",
                    color: hovPuja === i ? ACCENT : "#111111",
                    cursor: "pointer", transition: "border-color 0.2s, background 0.2s, color 0.2s",
                    fontFamily: "'Inter',sans-serif",
                    fontSize: "1.05rem", display: "flex", alignItems: "center", gap: "8px",
                  }}
                >
                  <Icon name={p.icon} size={16} color={hovPuja === i ? ACCENT : "#555544"}/> {p.name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Final verse + CTA ─────────────────────────────────────────────────── */}
      <section style={{
        padding: "80px 32px",
        borderTop: "1px solid rgba(201,149,58,0.3)",
        textAlign: "center",
        background: `linear-gradient(180deg, #FFFFFF 0%, rgba(${ACCENT_RGB},0.04) 100%)`,
        position: "relative",
      }}>
        {!isMobile && <div style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)", width: 600, height: 400,
          borderRadius: "50%",
          background: `radial-gradient(circle, rgba(${ACCENT_RGB},0.05) 0%, transparent 70%)`,
          pointerEvents: "none",
        }} />}
        <div style={{ maxWidth: 560, margin: "0 auto", position: "relative" }}>
          <div style={{
            fontFamily: "'Playfair Display',serif", fontWeight: "700",
            fontSize: "clamp(1.7rem, 3.5vw, 2.3rem)",
            color: "#000000", marginBottom: "18px", lineHeight: 1.3,
          }}>
            The Gita was spoken in the middle of a battlefield.
          </div>
          <div style={{
            fontFamily: "'Inter',sans-serif",
            color: "#333333", fontSize: "1.15rem",
            lineHeight: 1.85, marginBottom: "40px",
          }}>
            Arjuna was paralysed. He didn't need a lecture — he needed someone to speak directly to his exact moment. That is what this app does for you.
          </div>
          <button onClick={onGetStarted} style={{
            padding: "16px 48px", borderRadius: "10px",
            background: ACCENT,
            border: "none", color: "white",
            fontFamily: "'Inter',sans-serif", fontWeight: "600", fontSize: "1.1rem",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}>
            Begin your journey →
          </button>
          <div style={{ fontFamily: "'Inter',sans-serif", marginTop: "16px", fontSize: "1rem", color: "#555544" }}>
            Free · Takes 2 minutes to start
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer style={{
        padding: "32px 24px", borderTop: "1px solid rgba(201,149,58,0.3)",
        textAlign: "center", color: "#555544", fontSize: "1rem",
        fontFamily: "'Inter',sans-serif",
      }}>
        <div style={{ opacity: 0.5, marginBottom: "12px" }}>
          <span style={{ fontFamily: "'Noto Sans Devanagari',serif", marginRight: "8px" }}>ॐ</span>
          Vedsanga · वेदसङ्ग · Built with reverence for the Gita
        </div>
        <a href="/?priest_register=1" style={{
          color: ACCENT, fontSize: "1rem", textDecoration: "none",
          opacity: 0.7, borderBottom: `1px solid rgba(${ACCENT_RGB},0.3)`, paddingBottom: "1px",
        }}
          onMouseOver={e => e.target.style.opacity = 1}
          onMouseOut={e => e.target.style.opacity = 0.7}
        >
          <Icon name="trishul" size={13} color={ACCENT} style={{marginRight:5,verticalAlign:"middle"}}/> Are you a priest? Join VedSanga
        </a>
      </footer>

    </div>
  );
}
